/* eslint-disable no-undef */
import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { createTrackingClient } from '../../utils/trackingClient';
import { isWorkspacePreview } from '../../utils/workspacePreview';
import './VisitorTracking.css';

const client = createTrackingClient(process.env.REACT_APP_BACKEND_URL);
let bootstrap;
const pageEvents = new Map();
const OPEN_EVENT = 'gitme:open-cookie-preferences';
// After a choice the panel stays hidden; menus call this to reopen it.
export const openCookiePreferences = () => {
  if (!isWorkspacePreview()) window.dispatchEvent(new Event(OPEN_EVENT));
};
export const waitForTracking = () =>
  isWorkspacePreview()
    ? Promise.resolve()
    : Promise.race([
        (bootstrap || Promise.resolve())
          .then(() => client.idle())
          .catch(() => {}),
        new Promise(resolve => setTimeout(resolve, 4500))
      ]);

export default function VisitorTracking() {
  return isWorkspacePreview() ? null : <TrackingPreferences />;
}

function TrackingPreferences() {
  const location = useLocation();
  const [choices, setChoices] = useState({
    analytics: false,
    marketing: false,
    personalization: false
  });
  const [open, setOpen] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const record = () => {
    const key = location.key;
    if (pageEvents.has(key)) return pageEvents.get(key);
    const result = client.visit(
      location.pathname,
      new URLSearchParams(location.search).get('ref'),
      crypto.randomUUID()
    );
    pageEvents.set(key, result);
    result.catch(() => pageEvents.delete(key));
    return result;
  };
  useEffect(() => {
    let active = true;
    if (!bootstrap) bootstrap = client.bootstrap();
    bootstrap
      .then(value => {
        if (!active) return;
        setChoices(
          client.preferences || {
            analytics: value === true,
            marketing: false,
            personalization: false
          }
        );
        if (value === null) setOpen(true);
        if (value) record()?.catch(() => {});
      })
      .catch(() => {
        if (active) setOpen(true);
      });
    return () => {
      active = false;
    };
    // Each router location is a new view; rerenders must not duplicate it.
  }, [location.key]);
  useEffect(() => {
    const reopen = async () => {
      try {
        await client.bootstrap();
        setChoices(
          client.preferences || {
            analytics: client.analytics === true,
            marketing: false,
            personalization: false
          }
        );
      } catch {
        /* Keep the last displayed choices when temporarily offline. */
      }
      setError('');
      setOpen(true);
    };
    window.addEventListener(OPEN_EVENT, reopen);
    return () => window.removeEventListener(OPEN_EVENT, reopen);
  }, []);
  const choose = async value => {
    setBusy(true);
    setError('');
    try {
      await client.consent(value);
      const saved = client.preferences;
      bootstrap = Promise.resolve(saved.analytics);
      setChoices(saved);
      setOpen(false);
      if (saved.analytics) await record();
    } catch {
      setError('Could not save your preference. Please try again.');
    } finally {
      setBusy(false);
    }
  };
  if (!open) return null;
  return (
    <aside
      aria-label="Cookie preferences"
      className="cookie-preferences cookie-preferences--open"
    >
      <div className="cookie-preferences__heading">
        <strong>Cookie preferences</strong>
        <span>Privacy choices</span>
      </div>
      <p className="cookie-preferences__description">
        Choose which optional cookies GitMe may use. Analytics measures visits
        and credits the partner who introduced you. You can change these choices
        later from your account menu.
      </p>
      <a
        className="cookie-preferences__privacy-link"
        href="https://www.gitdotme.com/privacy-policy"
        target="_blank"
        rel="noreferrer"
      >
        Privacy policy
      </a>
      <div className="cookie-preferences__options">
        <div className="cookie-preferences__required">
          <span>Required cookies</span>
          <span>Always on</span>
        </div>
        {['analytics', 'marketing', 'personalization'].map(category => (
          <label key={category} className="cookie-preferences__option">
            <span>{category[0].toUpperCase() + category.slice(1)}</span>
            <input
              type="checkbox"
              checked={choices[category]}
              disabled={busy}
              onChange={event =>
                setChoices(current => ({
                  ...current,
                  [category]: event.target.checked
                }))
              }
            />
          </label>
        ))}
      </div>
      <div className="cookie-preferences__actions">
        <button
          type="button"
          className="cookie-preferences__button cookie-preferences__button--primary"
          disabled={busy}
          onClick={() =>
            choose({
              analytics: true,
              marketing: true,
              personalization: true
            })
          }
        >
          Accept all
        </button>
        <button
          type="button"
          className="cookie-preferences__button cookie-preferences__button--secondary"
          disabled={busy}
          onClick={() => choose(choices)}
        >
          Save choices
        </button>
        <button
          type="button"
          className="cookie-preferences__button cookie-preferences__button--text"
          disabled={busy}
          onClick={() =>
            choose({
              analytics: false,
              marketing: false,
              personalization: false
            })
          }
        >
          Only required cookies
        </button>
      </div>
      {error && (
        <p className="cookie-preferences__error" role="alert">
          {error}
        </p>
      )}
    </aside>
  );
}
